// ─── Choix par-dessus une feuille déjà ouverte (Core) ─────────────
// Quand une <Feuille> doit poser une question (à qui ? quoi ?), on NE PEUT PAS
// ouvrir une seconde feuille : deux <Modal> natives imbriquées figent l'écran,
// et une Feuille rendue dans le contenu d'une autre reste prisonnière de son
// conteneur. On rend donc un CALQUE — mais il doit obéir aux mêmes règles que
// toute modale de l'app, sinon l'utilisateur voit une carte bricolée :
//   • voile sombre sur toute la surface, fermeture au tap à côté ;
//   • poignée en haut, coins arrondis, ombre ;
//   • en-tête = emoji + titre à gauche, ✕ à droite (jamais un bouton
//     « Annuler » labellisé en bas — l'action de fermeture est une icône) ;
//   • hauteur COMPACTE : le calque s'ajuste à son contenu, il ne prend pas
//     l'écran pour trois lignes.
//
//   <CalqueChoix visible={…} titre="Confier à" emoji="🤝" onFermer={…}>
//     <LigneChoix …/>
//   </CalqueChoix>
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { useUI } from '../theme/PreferencesUI';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function CalqueChoix({ visible, titre, emoji, aide, onFermer, children }) {
  const UI = useUI();
  const s = useMemo(() => makeStyles(UI), [UI]);
  if (!visible) return null;
  return (
    <View style={s.calque}>
      <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={onFermer} accessibilityLabel="Fermer" />
      <View style={s.carte}>
        <View style={s.grabZone}><View style={s.grab} /></View>
        <View style={s.entete}>
          {!!emoji && <Text style={s.emoji}>{emoji}</Text>}
          <Text style={s.titre} numberOfLines={1}>{titre}</Text>
          <TouchableOpacity onPress={onFermer} hitSlop={HIT} accessibilityLabel="Fermer">
            <Text style={s.fermer}>✕</Text>
          </TouchableOpacity>
        </View>
        {!!aide && <Text style={s.aide}>{aide}</Text>}
        {/* Scrollable : une liste de membres peut être longue, la carte ne doit
            pas déborder de l'écran pour autant. */}
        <ScrollView style={{ maxHeight: 320 }} keyboardShouldPersistTaps="handled">{children}</ScrollView>
      </View>
    </View>
  );
}

// Ligne d'option : libellé à gauche, chevron à droite ; grisée et inerte quand
// elle est indisponible (on la garde VISIBLE — « rien ne s'affiche » est la
// pire des réponses).
export function LigneChoix({ libelle, sous, disponible = true, onPress }) {
  const UI = useUI();
  const s = useMemo(() => makeStyles(UI), [UI]);
  return (
    <TouchableOpacity style={s.ligne} activeOpacity={0.7} disabled={!disponible} onPress={onPress}>
      <View style={{ flex: 1 }}>
        <Text style={[s.ligneTxt, !disponible && s.off]} numberOfLines={2}>{libelle}</Text>
        {!!sous && <Text style={s.ligneSous} numberOfLines={2}>{sous}</Text>}
      </View>
      <Text style={[s.chev, !disponible && s.off]}>{disponible ? '›' : '—'}</Text>
    </TouchableOpacity>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  // Au-DESSUS de <Feuille> (zIndex 300 / elevation 48) : rendu au niveau de
  // l'écran à côté d'une feuille ouverte, ce calque passait DERRIÈRE elle —
  // l'appui semblait alors sans effet.
  calque:   { ...StyleSheet.absoluteFillObject, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.45)',
              zIndex: 400, elevation: 64 },
  carte:    { backgroundColor: UI.card, borderTopLeftRadius: 18, borderTopRightRadius: 18,
              paddingHorizontal: 16, paddingBottom: 22,
              shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 16, shadowOffset: { width: 0, height: -4 }, elevation: 12 },
  grabZone: { alignItems: 'center', paddingTop: 8, paddingBottom: 6 },
  grab:     { width: 40, height: 4, borderRadius: 2, backgroundColor: UI.border },
  entete:   { flexDirection: 'row', alignItems: 'center', gap: 8, paddingBottom: 8 },
  emoji:    { fontSize: 18 },
  titre:    { flex: 1, fontSize: 17, fontWeight: '800', color: UI.ink },
  fermer:   { fontSize: 20, color: UI.accent, fontWeight: '800' },
  aide:     { fontSize: 12, color: UI.muted, lineHeight: 17, marginBottom: 6 },
  ligne:    { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12,
              borderTopWidth: 0.5, borderTopColor: UI.border },
  ligneTxt: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  ligneSous:{ fontSize: 11.5, color: UI.muted, marginTop: 1 },
  chev:     { fontSize: 18, color: UI.accent, fontWeight: '800' },
  off:      { color: UI.faint },
});
